import { Page, PageBody, PageHeader } from "@/components/app-layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useBackfillOrganizationBucketCorsData } from "@/data/use-backfill-organization-bucket-cors-data";
import { useFileParseTimeout } from "@/data/use-file-parse-timeout-data";
import { useMigrateVectorDatabaseMetadata } from "@/data/use-vector-database-metadata-data";
import type {
  BackfillOrganizationBucketCorsResponseDto,
  FileParseTimeoutResponseDto,
  MigrateVectorDatabaseMetadataResponseDto,
} from "@/lib/gen-api";
import { useState } from "react";

export default function GlobalAdminTasksPage() {
  const { migrateVectorDatabaseMetadata, isPending } =
    useMigrateVectorDatabaseMetadata();
  const [open, setOpen] = useState(false);
  const [result, setResult] =
    useState<MigrateVectorDatabaseMetadataResponseDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = () => {
    setResult(null);
    setErrorMessage(null);
  };

  const continueMigration = async () => {
    setErrorMessage(null);
    try {
      setResult(await migrateVectorDatabaseMetadata());
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to migrate vector database metadata",
      );
    }
  };

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Tasks</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <div className="flex max-w-xl flex-col gap-3">
          <h2 className="text-lg font-semibold">Vector database</h2>
          <p className="text-foreground-8 text-sm">
            For older datapoints with workspace_uuid but without group_uuid, set
            group_uuid = workspace_uuid. Then backfill organization_uuid from
            each group.
          </p>
          <div>
            <Button
              onClick={() => {
                reset();
                setOpen(true);
              }}
            >
              Migrate vector database metadata
            </Button>
          </div>
          <FileParseTimeoutTask />
          <BucketCorsBackfillTask />
        </div>
      </PageBody>

      <AlertDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (isPending) {
            return;
          }
          setOpen(nextOpen);
          if (!nextOpen) {
            reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {result
                ? "Migration finished"
                : "Migrate vector database metadata"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {result
                ? "The vector database metadata migration finished."
                : "This copies workspace_uuid onto group_uuid for points that still use workspace_uuid, then writes each group's organization_uuid."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {result ? <MigrationResult result={result} /> : null}
          {errorMessage ? (
            <p className="text-destructive text-sm">{errorMessage}</p>
          ) : null}

          <AlertDialogFooter>
            {result ? (
              <Button onClick={() => setOpen(false)}>Close</Button>
            ) : (
              <>
                <AlertDialogCancel disabled={isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault(); // Prevent closing the dialog
                    void continueMigration();
                  }}
                  disabled={isPending}
                >
                  {isPending ? "Migrating..." : "Continue"}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}

const FileParseTimeoutTask = () => {
  const { timeoutStaleFileParses, isPending } = useFileParseTimeout();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<FileParseTimeoutResponseDto | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = () => {
    setResult(null);
    setErrorMessage(null);
  };

  const continueTimeout = async () => {
    setErrorMessage(null);
    try {
      setResult(await timeoutStaleFileParses());
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to time out stale file parses",
      );
    }
  };

  return (
    <>
      <h2 className="text-lg font-semibold">File parse</h2>
      <p className="text-foreground-8 text-sm">
        Mark files that are still parsing and were last updated more than 24
        hours ago as timed out.
      </p>
      <div>
        <Button
          onClick={() => {
            reset();
            setOpen(true);
          }}
        >
          Time out stale file parses
        </Button>
      </div>

      <AlertDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (isPending) {
            return;
          }
          setOpen(nextOpen);
          if (!nextOpen) {
            reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {result ? "File parse timeout finished" : "Time out stale parses"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {result
                ? "Files still parsing after 24 hours were marked as timed out."
                : "This sets error to timeout and stops parsing for files that have been parsing for more than 24 hours."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {result ? (
            <p className="text-sm">Updated files: {result.updatedCount}</p>
          ) : null}
          {errorMessage ? (
            <p className="text-destructive text-sm">{errorMessage}</p>
          ) : null}

          <AlertDialogFooter>
            {result ? (
              <Button onClick={() => setOpen(false)}>Close</Button>
            ) : (
              <>
                <AlertDialogCancel disabled={isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void continueTimeout();
                  }}
                  disabled={isPending}
                >
                  {isPending ? "Updating..." : "Continue"}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const BucketCorsBackfillTask = () => {
  const { backfillOrganizationBucketCors, isPending } =
    useBackfillOrganizationBucketCorsData();
  const [open, setOpen] = useState(false);
  const [result, setResult] =
    useState<BackfillOrganizationBucketCorsResponseDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = () => {
    setResult(null);
    setErrorMessage(null);
  };

  const continueBackfill = async () => {
    setErrorMessage(null);
    try {
      setResult(await backfillOrganizationBucketCors());
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to backfill organization bucket CORS",
      );
    }
  };

  return (
    <>
      <h2 className="text-lg font-semibold">Storage</h2>
      <p className="text-foreground-8 text-sm">
        Add the upload size header to the CORS rules on every organization
        bucket. A bucket with no CORS is listed as a failure.
      </p>
      <div>
        <Button
          onClick={() => {
            reset();
            setOpen(true);
          }}
        >
          Backfill bucket upload CORS
        </Button>
      </div>

      <AlertDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (isPending) {
            return;
          }
          setOpen(nextOpen);
          if (!nextOpen) {
            reset();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {result
                ? "Bucket CORS backfill finished"
                : "Backfill bucket upload CORS"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {result
                ? "Each organization bucket was checked."
                : "This adds x-goog-content-length-range to each organization bucket that already has CORS. Buckets that share a name are updated once."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {result ? <BucketCorsBackfillResult result={result} /> : null}
          {errorMessage ? (
            <p className="text-destructive text-sm">{errorMessage}</p>
          ) : null}

          <AlertDialogFooter>
            {result ? (
              <Button onClick={() => setOpen(false)}>Close</Button>
            ) : (
              <>
                <AlertDialogCancel disabled={isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void continueBackfill();
                  }}
                  disabled={isPending}
                >
                  {isPending ? "Updating..." : "Continue"}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const BucketCorsBackfillResult = ({
  result,
}: {
  result: BackfillOrganizationBucketCorsResponseDto;
}) => {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
      <dt>Updated buckets</dt>
      <dd>{result.updatedCount}</dd>
      <dt>Unchanged buckets</dt>
      <dd>{result.unchangedCount}</dd>
      <dt>Failures</dt>
      <dd>
        {result.failures.length === 0 ? (
          "None"
        ) : (
          <ul className="max-h-40 overflow-auto">
            {result.failures.map((failure) => (
              <li key={failure.bucketName}>
                {failure.bucketName}: {failure.message}
              </li>
            ))}
          </ul>
        )}
      </dd>
    </dl>
  );
};

const MigrationResult = ({
  result,
}: {
  result: MigrateVectorDatabaseMetadataResponseDto;
}) => {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
      <dt>Renamed points</dt>
      <dd>{result.renamedPointCount}</dd>
      <dt>Organizations backfilled</dt>
      <dd>{result.organizationBackfilledGroupCount}</dd>
      <dt>Missing groups</dt>
      <dd>
        {result.missingGroupUuids.length === 0 ? (
          "None"
        ) : (
          <ul className="max-h-40 overflow-auto">
            {result.missingGroupUuids.map((groupUuid) => (
              <li key={groupUuid}>{groupUuid}</li>
            ))}
          </ul>
        )}
      </dd>
    </dl>
  );
};
